import React, { useState, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, Loader2, FileSpreadsheet } from "lucide-react";

const LABELS = {
  lots: { title: "Importar lotes", desc: "CSV/XLSX com colunas: code, entry_date, qty_received, notes. Lotes com código já existente são ignorados." },
  expenses: { title: "Importar despesas", desc: "CSV/XLSX com colunas: category, competence_date, amount, description, status. A categoria deve já existir (pelo nome)." },
};

export default function ImportDialog({ entity, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const meta = LABELS[entity] || { title: "Importar", desc: "" };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/import/${entity}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Importação concluída: ${data.created} criado(s), ${data.skipped} ignorado(s).`);
      onDone?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`import-${entity}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-accent" /> {meta.title}</DialogTitle>
          <DialogDescription>{meta.desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.open(`${BACKEND}/api/import/${entity}/template`, "_blank")} data-testid={`import-${entity}-template`}><Download className="h-4 w-4 mr-2" /> Baixar modelo</Button>
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid={`import-${entity}-file`} />
            <Button onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`import-${entity}-upload`}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Enviar arquivo
            </Button>
          </div>
          {result && (
            <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm" data-testid={`import-${entity}-result`}>
              <p><strong>{result.created}</strong> criado(s) · <strong>{result.skipped}</strong> ignorado(s) · <strong>{result.errors?.length || 0}</strong> erro(s) de {result.total} linha(s)</p>
              {result.errors?.length > 0 && (
                <ul className="mt-2 max-h-40 overflow-auto text-xs text-red-600 list-disc pl-5">
                  {result.errors.map((e, i) => <li key={i}>Linha {e.row}: {e.error}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid={`import-${entity}-close`}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
